// Fiat-wallet endpoint functions.
//
// Endpoints take camelCase view-model Inputs and return camelCase view-model
// outputs. Snake_case lives ONLY between the network and the parser:
// outbound, this file lowers camelCase Inputs into PayloadDto shapes before
// sending; inbound, the parser converts response DTOs to view models.
//
// Review/approval of a fiat transaction has MOVED to deposit-requests /
// payout-requests sub-modules — see ../../depositRequests/api/ and
// ../../payoutRequests/api/.

import { adminApi } from '@core/api/httpClient'
import type { FiatTransactionsResponseDto, FiatWalletsResponseDto } from './walletContracts'
import type {
  FiatTransactionsResponse,
  FiatWalletsResponse,
  GetFiatWalletsParams,
  GetTransactionsParams,
  UpdateTransactionAmountInput,
} from '../types/walletTypes'
import { parseFiatTransactionsResponse, parseFiatWalletsResponse } from './walletParser'

const FIAT_WALLETS = '/api/wallets/fiat-wallets'
const WALLET_TRANSACTIONS = '/api/wallets/fiat/transactions'

export async function getFiatWallets(
  params: GetFiatWalletsParams = {},
): Promise<FiatWalletsResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = params.page.toString()
  if (params.limit) queryParams.limit = params.limit.toString()
  if (params.search) queryParams.search = params.search
  if (params.status) queryParams.status = params.status
  if (params.currencyCode) queryParams.currency_code = params.currencyCode

  const { data } = await adminApi.get<{ data?: FiatWalletsResponseDto } | FiatWalletsResponseDto>(
    FIAT_WALLETS,
    { params: queryParams },
  )
  const envelope =
    (data as { data?: FiatWalletsResponseDto })?.data ?? (data as FiatWalletsResponseDto)
  return parseFiatWalletsResponse(envelope ?? { rows: [], rowsCount: 0 })
}

export async function getWalletTransactions(
  params: GetTransactionsParams = {},
): Promise<FiatTransactionsResponse> {
  const url = params.investorId
    ? `${WALLET_TRANSACTIONS}/${params.investorId}`
    : WALLET_TRANSACTIONS

  // Server accepts snake_case query params.
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = params.page.toString()
  if (params.limit) queryParams.limit = params.limit.toString()
  if (params.transactionStatus) queryParams.transaction_status = params.transactionStatus
  if (params.transactionReason) queryParams.transaction_reason = params.transactionReason

  const { data } = await adminApi.get<{ data?: FiatTransactionsResponseDto }>(url, {
    params: queryParams,
  })
  return parseFiatTransactionsResponse(data?.data ?? { rows: [], total: 0 })
}

export async function updateTransactionAmount(input: UpdateTransactionAmountInput): Promise<void> {
  await adminApi.patch(`${WALLET_TRANSACTIONS}/${input.id}`, { amount: input.amount })
}
